import { cloneState, isActive } from '../board';
import type { HeroId, LineAxis, MatchState, PlayerId, Position, TileColor, UsePowerMove, WaveDir } from '../types';

/**
 * Tiles Foxfire turns into berries. The old untargeted scatter, leaned a
 * little toward the tap. A tight disk or a forced center lets the search
 * pick a jackpot and throws the duel off.
 */
export const FOXFIRE_COUNT = 24;
/**
 * How strongly distance reorders the scatter. Kept small: a heavier lean
 * turns the best of the glen into a berry bomb.
 */
export const FOXFIRE_BIAS = 0x01800000;
/** Reticle drawn around the tap while aiming. */
export const FOXFIRE_RADIUS = 2;

/** How long the player has to aim before the signature resolves itself. */
export const SIGNATURE_AIM_MS = 4000;
/** A drag shorter than this has not chosen a line or a wave yet. */
export const DRAG_LOCK_PX = 36;

const WAVE_DIRS: readonly WaveDir[] = ['up', 'down', 'left', 'right'];

export function signatureGesture(heroId: HeroId): 'tap' | 'drag' {
  if (heroId === 'bramble' || heroId === 'otto') return 'drag';
  const tune = SIGNATURE_TUNES[heroId];
  if (tune?.gesture === 'drag') return 'drag';
  return 'tap';
}

export function signaturePrompt(heroId: HeroId): string {
  if (heroId === 'pip') return 'Tap where Pip rolls';
  if (heroId === 'bramble') return 'Drag a row or column';
  if (heroId === 'cleo') return 'Tap where foxfire blooms';
  const tune = SIGNATURE_TUNES[heroId];
  if (tune) return tune.prompt;
  return 'Drag to aim the wave';
}

/** Column Pip rolls into when a bot aims a row. The middle of the glen. */
export function pipImpactColumn(width: number): number {
  return Math.floor((Math.max(1, width) - 1) / 2);
}

export function lockedAxis(dx: number, dy: number, min = DRAG_LOCK_PX): LineAxis | null {
  if (Math.abs(dx) < min && Math.abs(dy) < min) return null;
  return Math.abs(dx) >= Math.abs(dy) ? 'row' : 'column';
}

export function lockedDir(dx: number, dy: number, min = DRAG_LOCK_PX): WaveDir | null {
  if (Math.abs(dx) < min && Math.abs(dy) < min) return null;
  if (Math.abs(dx) >= Math.abs(dy)) return dx < 0 ? 'left' : 'right';
  return dy < 0 ? 'up' : 'down';
}

export function waveCells(state: MatchState, dir: WaveDir = 'down'): Position[] {
  const height = state.config.boardH;
  const width = state.config.boardW;
  const cells: Position[] = [];
  if (dir === 'up' || dir === 'down') {
    const rows = dir === 'up' ? [0, 1] : [height - 2, height - 1];
    for (const r of rows) {
      if (r < 0 || r >= height) continue;
      for (let c = 0; c < width; c++) {
        if (isActive(state, r, c) && state.board[r][c]) cells.push({ r, c });
      }
    }
    return cells;
  }
  const cols = dir === 'left' ? [0, 1] : [width - 2, width - 1];
  for (const c of cols) {
    if (c < 0 || c >= width) continue;
    for (let r = 0; r < height; r++) {
      if (isActive(state, r, c) && state.board[r][c]) cells.push({ r, c });
    }
  }
  return cells;
}

export function tunnelCells(state: MatchState, axis: LineAxis, index: number): Position[] {
  const cells: Position[] = [];
  if (axis === 'row') {
    if (index < 0 || index >= state.config.boardH) return cells;
    for (let c = 0; c < state.config.boardW; c++) {
      if (isActive(state, index, c) && state.board[index][c]) cells.push({ r: index, c });
    }
    return cells;
  }
  if (index < 0 || index >= state.config.boardW) return cells;
  for (let r = 0; r < state.config.boardH; r++) {
    if (isActive(state, r, index) && state.board[r][index]) cells.push({ r, c: index });
  }
  return cells;
}

function scatterKey(r: number, c: number, rngState: number): number {
  let x = (rngState ^ Math.imul(r + 1, 0x9e3779b1) ^ Math.imul(c + 1, 0x85ebca6b)) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x7feb352d);
  x = Math.imul(x ^ (x >>> 15), 0x846ca68b);
  return (x ^ (x >>> 16)) >>> 0;
}

/**
 * Tiles Foxfire converts. Distance is a light lean, not a disk, so two
 * neighboring taps share most of the bloom and the best tap stays near the
 * old single cast. A glen smaller than the scatter converts every tile.
 */
/** Scatter `count` tiles, leaned toward `center` by `bias`. A small board converts every tile. */
export function bloomCells(state: MatchState, center: Position, count: number, bias: number): Position[] {
  const area: Position[] = [];
  for (let r = 0; r < state.config.boardH; r++) {
    for (let c = 0; c < state.config.boardW; c++) {
      if (!isActive(state, r, c) || !state.board[r]?.[c]) continue;
      area.push({ r, c });
    }
  }
  if (area.length <= count) return area;
  return area
    .map((pos) => ({
      pos,
      score:
        scatterKey(pos.r, pos.c, state.rngState) -
        (Math.abs(pos.r - center.r) + Math.abs(pos.c - center.c)) * bias,
    }))
    .sort((a, b) => b.score - a.score || a.pos.r - b.pos.r || a.pos.c - b.pos.c)
    .slice(0, count)
    .map((item) => item.pos);
}

export function foxfireCells(state: MatchState, center: Position): Position[] {
  return bloomCells(state, center, FOXFIRE_COUNT, FOXFIRE_BIAS);
}

export function signatureMoveFromTap(heroId: HeroId, player: PlayerId, cell: Position): UsePowerMove | null {
  if (heroId === 'pip' || heroId === 'cleo') return { type: 'usePower', player, target: { r: cell.r, c: cell.c } };
  const tune = SIGNATURE_TUNES[heroId];
  if (tune?.gesture === 'tap') return { type: 'usePower', player, target: { r: cell.r, c: cell.c } };
  return null;
}

export function signatureMoveFromDrag(
  heroId: HeroId,
  player: PlayerId,
  drag: { dx: number; dy: number; anchor: Position; cell: Position | null },
): UsePowerMove | null {
  if (heroId === 'bramble') {
    const axis = lockedAxis(drag.dx, drag.dy);
    if (!axis) return null;
    const focus = drag.cell ?? drag.anchor;
    const index = axis === 'row' ? focus.r : focus.c;
    return {
      type: 'usePower',
      player,
      axis,
      target: axis === 'row' ? { r: index, c: 0 } : { r: 0, c: index },
    };
  }
  if (heroId === 'otto') {
    const dir = lockedDir(drag.dx, drag.dy);
    if (!dir) return null;
    return { type: 'usePower', player, dir };
  }
  const tune = SIGNATURE_TUNES[heroId];
  if (!tune || tune.gesture !== 'drag') return null;
  if (tune.kind === 'line') {
    const axis = lockedAxis(drag.dx, drag.dy);
    if (!axis) return null;
    const focus = drag.cell ?? drag.anchor;
    const index = axis === 'row' ? focus.r : focus.c;
    return {
      type: 'usePower',
      player,
      axis,
      target: axis === 'row' ? { r: index, c: 0 } : { r: 0, c: index },
    };
  }
  const dir = lockedDir(drag.dx, drag.dy);
  if (!dir) return null;
  return { type: 'usePower', player, dir };
}

export interface SignaturePreview {
  /** Cells the signature will touch. */
  cells: Position[];
  /** Softer ring around an area, when the cast is wider than the sample. */
  glow: Position[];
}

export interface AimPointer {
  cell: Position | null;
  anchor: Position | null;
  dx: number;
  dy: number;
}

/** Highlight for the aim moment. Does not advance the match RNG. */
export function signaturePreview(state: MatchState, heroId: HeroId, pointer: AimPointer): SignaturePreview {
  if (heroId === 'pip') {
    if (!pointer.cell) return { cells: [], glow: [] };
    return { cells: tunnelCells(state, 'row', pointer.cell.r), glow: [pointer.cell] };
  }
  if (heroId === 'cleo') {
    if (!pointer.cell || !state.board[pointer.cell.r]?.[pointer.cell.c]) return { cells: [], glow: [] };
    const cells = foxfireCells(cloneState(state), pointer.cell);
    const glow: Position[] = [];
    for (let r = pointer.cell.r - FOXFIRE_RADIUS; r <= pointer.cell.r + FOXFIRE_RADIUS; r++) {
      for (let c = pointer.cell.c - FOXFIRE_RADIUS; c <= pointer.cell.c + FOXFIRE_RADIUS; c++) {
        if (!isActive(state, r, c) || !state.board[r]?.[c]) continue;
        if (Math.max(Math.abs(r - pointer.cell.r), Math.abs(c - pointer.cell.c)) > FOXFIRE_RADIUS) continue;
        glow.push({ r, c });
      }
    }
    return { cells, glow };
  }
  if (heroId === 'bramble') {
    if (!pointer.anchor) return { cells: [], glow: [] };
    const axis = lockedAxis(pointer.dx, pointer.dy);
    if (!axis) return { cells: [], glow: [] };
    const focus = pointer.cell ?? pointer.anchor;
    const index = axis === 'row' ? focus.r : focus.c;
    return { cells: tunnelCells(state, axis, index), glow: [] };
  }
  const tune = SIGNATURE_TUNES[heroId];
  if (tune) return previewTune(state, tune, pointer);
  const dir = lockedDir(pointer.dx, pointer.dy);
  if (!dir) return { cells: [], glow: [] };
  return { cells: waveCells(state, dir), glow: [] };
}

export function isWaveDir(value: string | undefined): value is WaveDir {
  return !!value && (WAVE_DIRS as readonly string[]).includes(value);
}

export { WAVE_DIRS };

/**
 * Modest variations on the class signature. Pip, Bramble, Cleo, and Otto keep
 * their original casts and are not listed here.
 */
type RowTune = { gesture: 'tap'; kind: 'row'; inset: number; extra: boolean; prompt: string };
type LineTune = { gesture: 'drag'; kind: 'line'; inset: number; neighbor: boolean; prompt: string };
type BloomTune = { gesture: 'tap'; kind: 'bloom'; count: number; bias: number; color: TileColor; prompt: string };
type WaveTune = { gesture: 'drag'; kind: 'wave'; depth: number; shift: number; trim: number; prompt: string };
export type SignatureTune = RowTune | LineTune | BloomTune | WaveTune;

const lean = (factor: number) => Math.round(FOXFIRE_BIAS * factor);

export const SIGNATURE_TUNES: Partial<Record<HeroId, SignatureTune>> = {
  bruno: { gesture: 'tap', kind: 'row', inset: 0, extra: true, prompt: 'Tap where the skillet lands' },
  tuck: { gesture: 'tap', kind: 'row', inset: 1, extra: false, prompt: 'Tap the row the plank holds' },
  thistle: { gesture: 'drag', kind: 'line', inset: 1, neighbor: false, prompt: 'Drag a shorter row or column' },
  marlow: { gesture: 'drag', kind: 'line', inset: 0, neighbor: true, prompt: 'Drag a row or column' },
  wren: { gesture: 'tap', kind: 'bloom', count: 20, bias: lean(1.5), color: 'moonstone', prompt: 'Tap where moon glare blooms' },
  juniper: { gesture: 'tap', kind: 'bloom', count: 22, bias: lean(0.7), color: 'berry', prompt: 'Tap where mask dust blooms' },
  lumi: { gesture: 'drag', kind: 'wave', depth: 2, shift: 1, trim: 0, prompt: 'Drag to aim the glide' },
  barley: { gesture: 'drag', kind: 'wave', depth: 2, shift: 0, trim: 1, prompt: 'Drag to burst the dam' },
  fern: { gesture: 'tap', kind: 'bloom', count: 16, bias: lean(1.2), color: 'blossom', prompt: 'Tap where the fernbed blooms' },
  posy: { gesture: 'tap', kind: 'bloom', count: 14, bias: lean(2), color: 'blossom', prompt: 'Tap where petals gather' },
  dash: { gesture: 'tap', kind: 'bloom', count: 18, bias: lean(0.75), color: 'blossom', prompt: 'Tap where the canopy lands' },
};

export function signatureTune(heroId: HeroId): SignatureTune | null {
  return SIGNATURE_TUNES[heroId] ?? null;
}

export interface TunedAim {
  target?: Position;
  axis?: LineAxis;
  dir?: WaveDir;
}

/** Cells a tuned signature touches. Null for Pip, Bramble, Cleo, and Otto. */
export function tunedCells(state: MatchState, heroId: HeroId, aim: TunedAim): Position[] | null {
  const tune = SIGNATURE_TUNES[heroId];
  if (!tune) return null;
  return cellsForTune(state, tune, aim);
}

function insetLine(cells: Position[], axis: LineAxis, inset: number, span: number): Position[] {
  if (inset <= 0) return cells;
  return cells.filter((cell) => {
    const index = axis === 'row' ? cell.c : cell.r;
    return index >= inset && index < span - inset;
  });
}

function towardCenter(state: MatchState, axis: LineAxis, cell: Position): Position | null {
  if (axis === 'row') {
    const mid = Math.floor((state.config.boardH - 1) / 2);
    let r = cell.r < mid ? cell.r + 1 : cell.r > mid ? cell.r - 1 : cell.r + 1;
    if (r < 0 || r >= state.config.boardH) r = cell.r - 1;
    if (r < 0 || r >= state.config.boardH) return null;
    return { r, c: cell.c };
  }
  const mid = Math.floor((state.config.boardW - 1) / 2);
  let c = cell.c < mid ? cell.c + 1 : cell.c > mid ? cell.c - 1 : cell.c + 1;
  if (c < 0 || c >= state.config.boardW) c = cell.c - 1;
  if (c < 0 || c >= state.config.boardW) return null;
  return { r: cell.r, c };
}

function pushIfTile(state: MatchState, cells: Position[], pos: Position | null): void {
  if (!pos || !isActive(state, pos.r, pos.c) || !state.board[pos.r]?.[pos.c]) return;
  if (cells.some((cell) => cell.r === pos.r && cell.c === pos.c)) return;
  cells.push(pos);
}

/** Two edge lines, shifted inward and optionally trimmed at both ends. */
export function waveBand(state: MatchState, dir: WaveDir, depth = 2, shift = 0, trim = 0): Position[] {
  const height = state.config.boardH;
  const width = state.config.boardW;
  const cells: Position[] = [];
  const push = (r: number, c: number) => {
    if (isActive(state, r, c) && state.board[r]?.[c]) cells.push({ r, c });
  };
  if (dir === 'up' || dir === 'down') {
    const start = dir === 'up' ? shift : height - depth - shift;
    for (let i = 0; i < depth; i++) {
      const r = start + i;
      if (r < 0 || r >= height) continue;
      for (let c = trim; c < width - trim; c++) push(r, c);
    }
    return cells;
  }
  const start = dir === 'left' ? shift : width - depth - shift;
  for (let i = 0; i < depth; i++) {
    const c = start + i;
    if (c < 0 || c >= width) continue;
    for (let r = trim; r < height - trim; r++) push(r, c);
  }
  return cells;
}

function cellsForTune(state: MatchState, tune: SignatureTune, aim: TunedAim): Position[] {
  if (tune.kind === 'row') {
    const row = aim.target?.r ?? 0;
    const cells = insetLine(tunnelCells(state, 'row', row), 'row', tune.inset, state.config.boardW);
    if (tune.extra) {
      const impact = pipImpactColumn(state.config.boardW);
      pushIfTile(state, cells, towardCenter(state, 'row', { r: row, c: impact }));
    }
    return cells;
  }
  if (tune.kind === 'line') {
    const axis = aim.axis === 'row' ? 'row' : 'column';
    const index = axis === 'row' ? (aim.target?.r ?? 0) : (aim.target?.c ?? 0);
    const span = axis === 'row' ? state.config.boardW : state.config.boardH;
    const cells = insetLine(tunnelCells(state, axis, index), axis, tune.inset, span);
    if (tune.neighbor && cells.length > 0) {
      const mid = cells[Math.floor((cells.length - 1) / 2)];
      pushIfTile(state, cells, towardCenter(state, axis, mid));
    }
    return cells;
  }
  if (tune.kind === 'bloom') {
    const center = aim.target ?? {
      r: Math.floor((state.config.boardH - 1) / 2),
      c: Math.floor((state.config.boardW - 1) / 2),
    };
    return bloomCells(state, center, tune.count, tune.bias);
  }
  return waveBand(state, aim.dir ?? 'down', tune.depth, tune.shift, tune.trim);
}

function bloomGlow(state: MatchState, center: Position): Position[] {
  const glow: Position[] = [];
  for (let r = center.r - FOXFIRE_RADIUS; r <= center.r + FOXFIRE_RADIUS; r++) {
    for (let c = center.c - FOXFIRE_RADIUS; c <= center.c + FOXFIRE_RADIUS; c++) {
      if (!isActive(state, r, c) || !state.board[r]?.[c]) continue;
      if (Math.max(Math.abs(r - center.r), Math.abs(c - center.c)) > FOXFIRE_RADIUS) continue;
      glow.push({ r, c });
    }
  }
  return glow;
}

function previewTune(state: MatchState, tune: SignatureTune, pointer: AimPointer): SignaturePreview {
  if (tune.kind === 'row') {
    if (!pointer.cell) return { cells: [], glow: [] };
    return { cells: cellsForTune(state, tune, { target: pointer.cell }), glow: [pointer.cell] };
  }
  if (tune.kind === 'bloom') {
    if (!pointer.cell || !state.board[pointer.cell.r]?.[pointer.cell.c]) return { cells: [], glow: [] };
    return {
      cells: cellsForTune(cloneState(state), tune, { target: pointer.cell }),
      glow: bloomGlow(state, pointer.cell),
    };
  }
  if (tune.kind === 'line') {
    if (!pointer.anchor) return { cells: [], glow: [] };
    const axis = lockedAxis(pointer.dx, pointer.dy);
    if (!axis) return { cells: [], glow: [] };
    const focus = pointer.cell ?? pointer.anchor;
    const index = axis === 'row' ? focus.r : focus.c;
    const target = axis === 'row' ? { r: index, c: 0 } : { r: 0, c: index };
    return { cells: cellsForTune(state, tune, { axis, target }), glow: [] };
  }
  const dir = lockedDir(pointer.dx, pointer.dy);
  if (!dir) return { cells: [], glow: [] };
  return { cells: cellsForTune(state, tune, { dir }), glow: [] };
}
